import { useCallback, useMemo, useState } from 'react'
import { useAuth } from './useAuth'

// 一覧画面の列表示/非表示をユーザーごとに保持するフック（顧客指摘 No.30: 受給者検索の列が横に長すぎる）。
// useSearchPersistence と同じ流儀で localStorage に保存し、ログインユーザー(cognitoSub)でスコープする。
// 共用 PC で他ユーザの列設定が混ざらないようにするため、また画面ごとに別管理するため screenKey で分ける。
//
// 保存値は「非表示にした列ID集合（hidden）」とする。表示状態マップではなく hidden を保存することで、
// 将来 allColumnIds に新しい列が増えても（hidden に入っていない＝）既定で表示になる。
const STORAGE_PREFIX = 'pams.columns.'

function readHidden(storageKey: string): Set<string> {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return new Set()
    const saved = window.localStorage.getItem(storageKey)
    if (!saved) return new Set()
    const parsed = JSON.parse(saved) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((id): id is string => typeof id === 'string'))
  } catch {
    // JSON 破損・privacyMode・SSR 等は既定（全表示）にフォールバック。
    return new Set()
  }
}

export interface UseColumnVisibilityOptions {
  /** 常に表示し、チェックを外せない列ID（例: 操作列）。 */
  alwaysVisible?: string[]
}

export interface ColumnVisibilityState {
  /** allColumnIds の順序を保ったまま、表示中の列IDだけに絞った配列。 */
  visibleIds: string[]
  /** 指定列が現在表示中か。 */
  isVisible: (id: string) => boolean
  /** 指定列を表示/非表示に切り替える（alwaysVisible 列は常に表示で無視）。 */
  setVisible: (id: string, on: boolean) => void
  /** すべて表示に戻す（hidden を空にする）。 */
  reset: () => void
  /** 現在非表示にしている列数。 */
  hiddenCount: number
}

export function useColumnVisibility(
  screenKey: string,
  allColumnIds: readonly string[],
  options?: UseColumnVisibilityOptions,
): ColumnVisibilityState {
  const { session } = useAuth()
  const sub = session?.cognitoSub ?? 'anon'
  const storageKey = `${STORAGE_PREFIX}${sub}.${screenKey}`

  // alwaysVisible は呼び出し側でインライン配列を渡しても安定参照になるよう join でメモ化キーにする。
  const alwaysVisibleKey = (options?.alwaysVisible ?? []).join(' ')
  const alwaysVisible = useMemo(
    () => new Set(alwaysVisibleKey ? alwaysVisibleKey.split(' ') : []),
    [alwaysVisibleKey],
  )

  // allColumnIds の所属判定用。ロール等で columnOrder に含まれない列ID（例: 非管理者の
  // municipality 列）に対し isVisible が true を返すと、colgroup/幅/colSpan は列を含まないのに
  // セルだけ描画され列ズレ＋ロール漏洩になる。所属しない列は常に非表示として扱う。
  const allColumnIdSet = useMemo(() => new Set(allColumnIds), [allColumnIds])

  const [hidden, setHidden] = useState<Set<string>>(() => readHidden(storageKey))

  const persist = useCallback(
    (next: Set<string>) => {
      setHidden(next)
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(storageKey, JSON.stringify([...next]))
        }
      } catch {
        // ignore storage errors (quota, private mode, etc.)
      }
    },
    [storageKey],
  )

  const setVisible = useCallback(
    (id: string, on: boolean) => {
      // alwaysVisible 列は常に表示。非表示要求は無視する。
      if (alwaysVisible.has(id)) return
      setHidden((prev) => {
        const next = new Set(prev)
        if (on) next.delete(id)
        else next.add(id)
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(storageKey, JSON.stringify([...next]))
          }
        } catch {
          // ignore storage errors (quota, private mode, etc.)
        }
        return next
      })
    },
    [alwaysVisible, storageKey],
  )

  const reset = useCallback(() => {
    persist(new Set())
  }, [persist])

  const isVisible = useCallback(
    (id: string) => allColumnIdSet.has(id) && (alwaysVisible.has(id) || !hidden.has(id)),
    [allColumnIdSet, alwaysVisible, hidden],
  )

  const visibleIds = useMemo(
    () => allColumnIds.filter((id) => alwaysVisible.has(id) || !hidden.has(id)),
    [allColumnIds, alwaysVisible, hidden],
  )

  // alwaysVisible は外せないため hiddenCount から除外する（実際に隠せている列だけを数える）。
  const hiddenCount = useMemo(
    () => [...hidden].filter((id) => !alwaysVisible.has(id) && allColumnIds.includes(id)).length,
    [hidden, alwaysVisible, allColumnIds],
  )

  return { visibleIds, isVisible, setVisible, reset, hiddenCount }
}

export default useColumnVisibility
